import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowSquareOut } from "@phosphor-icons/react";

export default function SiteContent() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [legalDocs, setLegalDocs] = useState([]);
  const [savingLegal, setSavingLegal] = useState(null);
  const [slots, setSlots] = useState({ quarter_label: "Q2 2026", total_slots: 2, taken_slots: 0, note: "" });
  const [savingSlots, setSavingSlots] = useState(false);

  const load = async () => {
    setLoading(true);
    const [{ data: site }, { data: legal }, { data: slotsData }] = await Promise.all([
      api.get("/site-content"),
      api.get("/admin/legal").catch(() => ({ data: [] })),
      api.get("/co-innovation/slots").catch(() => ({ data: null })),
    ]);
    setContent(site || {});
    setLegalDocs(legal || []);
    if (slotsData) setSlots(slotsData);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveKey = async (key, value) => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", { key, value });
      toast.success(`Saved ${key}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const saveLegal = async (slug, title, body) => {
    setSavingLegal(slug);
    try {
      const { data } = await api.put(`/admin/legal/${slug}`, { title, body });
      toast.success(`Saved ${title} · v${data.version}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingLegal(null); }
  };

  const updateLegal = (slug, patch) => {
    setLegalDocs(legalDocs.map((d) => (d.slug === slug ? { ...d, ...patch } : d)));
  };

  const saveSlots = async () => {
    setSavingSlots(true);
    try {
      const { data } = await api.put("/admin/co-innovation/slots", {
        quarter_label: slots.quarter_label,
        total_slots: Number(slots.total_slots) || 0,
        taken_slots: Number(slots.taken_slots) || 0,
        note: slots.note || "",
      });
      toast.success(`Saved · ${data.slots_left} slot${data.slots_left === 1 ? "" : "s"} left`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSavingSlots(false); }
  };

  if (loading) return <div className="p-10 font-mono text-sm text-zinc-500">Loading…</div>;

  const hero = content.home_hero || {};
  const hiw = content.how_it_works || { steps: [] };
  const footer = content.footer || {};

  return (
    <div className="p-8 md:p-10 max-w-4xl" data-testid="admin-site-content">
      <p className="overline mb-2">Content management</p>
      <h1 className="text-3xl font-bold tracking-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>Site content</h1>

      <section className="bg-white border border-zinc-200 p-6 mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest font-mono text-zinc-600 mb-4">Homepage hero</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Overline" value={hero.overline} onChange={(v) => setContent({ ...content, home_hero: { ...hero, overline: v } })} />
          <Input label="Primary CTA label" value={hero.primary_cta_label} onChange={(v) => setContent({ ...content, home_hero: { ...hero, primary_cta_label: v } })} />
          <Input label="Primary CTA link" value={hero.primary_cta_href} onChange={(v) => setContent({ ...content, home_hero: { ...hero, primary_cta_href: v } })} />
          <Input label="Secondary CTA label" value={hero.secondary_cta_label} onChange={(v) => setContent({ ...content, home_hero: { ...hero, secondary_cta_label: v } })} />
          <Input label="Secondary CTA link" value={hero.secondary_cta_href} onChange={(v) => setContent({ ...content, home_hero: { ...hero, secondary_cta_href: v } })} />
        </div>
        <Textarea label="Headline" value={hero.headline} onChange={(v) => setContent({ ...content, home_hero: { ...hero, headline: v } })} />
        <Textarea label="Subheadline" value={hero.subheadline} onChange={(v) => setContent({ ...content, home_hero: { ...hero, subheadline: v } })} />
        <button onClick={() => saveKey("home_hero", content.home_hero)} disabled={saving} className="btn-signal" data-testid="save-home-hero">Save hero</button>
      </section>

      <section className="bg-white border border-zinc-200 p-6 mb-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest font-mono text-zinc-600 mb-4">How it works (4 steps)</h2>
        {(hiw.steps || []).map((s, i) => (
          <div key={i} className="mb-4 border-b border-zinc-100 pb-4 last:border-b-0">
            <Input label={`Step ${i + 1} title`} value={s.title} onChange={(v) => {
              const steps = [...hiw.steps]; steps[i] = { ...s, title: v };
              setContent({ ...content, how_it_works: { steps } });
            }} />
            <Textarea label={`Step ${i + 1} body`} value={s.body} onChange={(v) => {
              const steps = [...hiw.steps]; steps[i] = { ...s, body: v };
              setContent({ ...content, how_it_works: { steps } });
            }} />
          </div>
        ))}
        <button onClick={() => saveKey("how_it_works", content.how_it_works)} disabled={saving} className="btn-signal" data-testid="save-how-it-works">Save steps</button>
      </section>

      <section className="bg-white border border-zinc-200 p-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest font-mono text-zinc-600 mb-4">Footer</h2>
        <Input label="Contact email" value={footer.contact_email} onChange={(v) => setContent({ ...content, footer: { ...footer, contact_email: v } })} />
        <Input label="Address / Location" value={footer.address} onChange={(v) => setContent({ ...content, footer: { ...footer, address: v } })} />
        <button onClick={() => saveKey("footer", content.footer)} disabled={saving} className="btn-signal" data-testid="save-footer">Save footer</button>
      </section>

      <section className="bg-white border border-zinc-200 p-6 mt-6" data-testid="coinnovation-slots-section">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest font-mono text-zinc-600">Co-innovation slots</h2>
            <p className="text-xs text-zinc-500 mt-1">Live founding-partner counter shown on <a href="/co-innovation" target="_blank" rel="noreferrer" className="underline">/co-innovation</a>. Three slots open every six months. Bump "Taken" up when you sign a partner — the public counter ticks down instantly.</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Slots left</div>
            <div className="text-3xl font-black tabular-nums" style={{ color: "hsl(var(--appistry-signal))" }} data-testid="slots-remaining">
              {Math.max(0, (Number(slots.total_slots) || 0) - (Number(slots.taken_slots) || 0))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Input label="Period label (shown on public page · e.g. H1 2026)" value={slots.quarter_label} onChange={(v) => setSlots({ ...slots, quarter_label: v })} />
          <label className="block text-sm mb-3">
            <span className="font-semibold text-zinc-800 mb-1 block">Total slots this period</span>
            <input type="number" min="0" value={slots.total_slots ?? ""} onChange={(e) => setSlots({ ...slots, total_slots: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="slots-total-input" />
          </label>
          <label className="block text-sm mb-3">
            <span className="font-semibold text-zinc-800 mb-1 block">Taken (founding partners signed)</span>
            <input type="number" min="0" value={slots.taken_slots ?? ""} onChange={(e) => setSlots({ ...slots, taken_slots: e.target.value })} className="w-full border border-zinc-200 px-3 py-2 bg-white" data-testid="slots-taken-input" />
          </label>
        </div>
        <Input label="Internal note (not shown publicly)" value={slots.note} onChange={(v) => setSlots({ ...slots, note: v })} />
        <button onClick={saveSlots} disabled={savingSlots} className="btn-signal" data-testid="save-slots">
          {savingSlots ? "Saving…" : "Save slot counter"}
        </button>
      </section>

      <section className="bg-white border border-zinc-200 p-6 mt-6" data-testid="legal-editor-section">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest font-mono text-zinc-600">Legal documents</h2>
            <p className="text-xs text-zinc-500 mt-1">Privacy Policy & Terms of Use. Markdown supported (headings, lists, links, tables, bold). Version is auto-bumped on save.</p>
          </div>
        </div>

        {legalDocs.length === 0 && (
          <p className="text-sm text-zinc-500 font-mono mt-4">No legal documents yet. They are seeded automatically on backend startup.</p>
        )}

        {legalDocs.map((doc) => (
          <div key={doc.slug} className="mt-6 pt-6 border-t border-zinc-100 first:border-t-0 first:pt-0 first:mt-4" data-testid={`legal-editor-${doc.slug}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-900 text-white px-2 py-1">{doc.slug}</span>
                <span className="text-xs font-mono text-zinc-500">v{doc.version} · Updated {doc.updated_at?.slice(0, 10)}</span>
              </div>
              <a href={`/${doc.slug}`} target="_blank" rel="noreferrer" className="text-xs font-mono text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid={`legal-preview-${doc.slug}`}>
                Preview <ArrowSquareOut size={12} weight="bold" />
              </a>
            </div>
            <Input label="Title" value={doc.title} onChange={(v) => updateLegal(doc.slug, { title: v })} />
            <label className="block text-sm mb-3">
              <span className="font-semibold text-zinc-800 mb-1 block">Body (markdown)</span>
              <textarea
                rows={20}
                value={doc.body || ""}
                onChange={(e) => updateLegal(doc.slug, { body: e.target.value })}
                className="w-full border border-zinc-200 px-3 py-2 bg-white font-mono text-xs leading-relaxed"
                data-testid={`legal-body-${doc.slug}`}
              />
            </label>
            <button
              onClick={() => saveLegal(doc.slug, doc.title, doc.body)}
              disabled={savingLegal === doc.slug}
              className="btn-signal"
              data-testid={`save-legal-${doc.slug}`}
            >
              {savingLegal === doc.slug ? "Saving…" : `Save ${doc.title}`}
            </button>
          </div>
        ))}
      </section>
    </div>
  );
}

function Input({ label, value, onChange }) {
  return (
    <label className="block text-sm mb-3">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 bg-white" />
    </label>
  );
}
function Textarea({ label, value, onChange }) {
  return (
    <label className="block text-sm mb-3">
      <span className="font-semibold text-zinc-800 mb-1 block">{label}</span>
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-200 px-3 py-2 bg-white" />
    </label>
  );
}
